import { api } from './client';

export type PedidoEstado =
  | 'abierto'
  | 'en_preparacion'
  | 'listo'
  | 'entregado'
  | 'facturado'
  | 'pagado'
  | 'cancelado';

export interface Pedido {
  id: number;
  sede_id: number;
  mesa_id: number | null;
  mesa_nombre?: string;
  grupo_id: number | null;
  grupo_nombre?: string;
  mesero_id: number | null;
  mesero_nombre?: string;
  /** CÓMO se entrega. Ortogonal a `origen` (mig. 047). */
  tipo: 'mesa' | 'para_llevar' | 'domicilio';
  /** DE DÓNDE vino. Un pedido del ecommerce puede ser para recoger, y uno del
   *  POS puede ser a domicilio: son dos preguntas distintas y meterlas en una
   *  sola columna obliga a perder una de las dos verdades. */
  origen?: 'pos' | 'ecommerce';
  cliente_email?: string | null;

  /** Envío asociado (mig. 047). Es lo que permite que la cola diga quién
   *  recoge en vez de mostrar una mesa que no existe. */
  envio_id?: number | null;
  envio_estado?: string | null;
  mensajero_nombre?: string | null;
  mensajero_telefono?: string | null;
  envio_tracking_url?: string | null;
  /** Quién retira el pedido (mig. 038). Ambos OPCIONALES: en un local chico la
   *  referencia (#0042) alcanza y obligarlos hace que el cajero escriba "x". */
  cliente_nombre?: string | null;
  cliente_telefono?: string | null;
  estado: PedidoEstado;
  num_personas: number | null;
  subtotal: string;
  propina: string;
  total: string;
  venta_id: number | null;
  cufe?: string | null;
  estado_dian?: string | null;
  notas: string | null;
  referencia: string;
  created_at: string;
  facturado_at: string | null;
  pagado_at: string | null;
  cancelado_at: string | null;
}

/** Un grupo de modificadores tal como se guarda en el JSONB del item.
 *  `tipo` lo agregó la mig. 033: 'exclusion' = "sin cebolla" (sin precio),
 *  'adicion' = "+ queso" (con precio y con descuento de stock). Los grupos
 *  configurados a mano (mig. 007) vienen sin `tipo`. */
export interface ModificadorItem {
  modificador_id: number | null;
  nombre: string;
  tipo?: 'exclusion' | 'adicion';
  opciones: Array<{
    opcion_id: number | null;
    nombre: string;
    precio_extra: number;
    /** Insumo de la receta (solo en exclusiones/adiciones de la mig. 033). */
    item_id?: number;
    /** Cantidad a descontar del stock por cada adición. */
    cantidad?: number;
    /** Mig. 059: cuántas veces se eligió la opción (Trifásico: 2× pollo). Sin = 1. */
    veces?: number;
  }>;
}

export interface PedidoItem {
  id: number;
  pedido_id: number;
  producto_id: number | null;
  codigo: string;
  nombre: string;
  cantidad: string;
  precio_unitario: string;
  subtotal: string;
  notas: string | null;
  /** Cortesía: el cliente paga $0; valor_cortesia guarda el precio de carta. */
  es_cortesia?: boolean;
  valor_cortesia?: string | null;
  /** Cortesía a nombre de otro usuario (id, ago-2026). */
  cortesia_de?: number | null;
  /** Modificadores del item. Además de los grupos configurados a mano
   *  (mig. 007), acá viajan las EXCLUSIONES y ADICIONES derivadas de la
   *  receta (mig. 033), distinguidas por `tipo`. */
  modificadores?: ModificadorItem[];
}

export interface PedidoConItems extends Pedido {
  items: PedidoItem[];
}

export interface CrearPedidoInput {
  sede_id: number;
  mesa_id?: number | null;
  grupo_id?: number | null;
  tipo?: 'mesa' | 'para_llevar' | 'domicilio';
  num_personas?: number;
  notas?: string;
  /** Quién retira, en pedidos que no son de mesa (mig. 038). Opcionales. */
  cliente_nombre?: string | null;
  cliente_telefono?: string | null;
  /** Quién ATENDIÓ la mesa. Si no se manda, queda quien registra el pedido. */
  mesero_id?: number | null;
  items: Array<{
    producto_id?: number | null;
    codigo?: string;
    nombre?: string;
    cantidad: number;
    precio_unitario?: number;
    notas?: string;
    /** true = cortesía: $0 al cliente, trackeada, sale del inventario normal. */
    es_cortesia?: boolean;
    /** Cortesía "a nombre de" otro usuario de la plataforma (ago-2026). */
    cortesia_de?: number | null;
    modificadores?: ModificadorItem[];
  }>;
}

/** Corregir quién atendió, mientras el pedido no esté facturado. */
export const asignarMesero = (pedidoId: number, meseroId: number) =>
  api.patch<Pedido>(`/api/pedidos/${pedidoId}/mesero`, { mesero_id: meseroId });

// Crear, agregar platos, facturar y pagar van con llave contra duplicados
// (api.postIdempotente, mig. 063): si la red se cae a mitad, el reintento no
// crea otro pedido ni otra línea.
export const crearPedido = (data: CrearPedidoInput) =>
  api.postIdempotente<Pedido & {
    warnings: Array<{ producto_id?: number; msg: string }>;
    items_ids: number[];
  }>('/api/pedidos', data);

export const agregarItemsPedido = (id: number, items: CrearPedidoInput['items']) =>
  api.postIdempotente<{
    id: number;
    referencia: string;
    subtotal: number;
    total: number;
    items_agregados: number;
    /** Ids de las líneas creadas — el POS los usa para ofrecer la
     *  personalización sobre el item recién agregado (mig. 033). */
    items_ids: number[];
    warnings: Array<{ producto_id?: number; msg: string }>;
  }>(`/api/pedidos/${id}/items`, { items });

// PATCH /api/pedidos/:id/items/:itemId — cambia la cantidad de una línea.
// Backend rechaza el cambio si el pedido está pagado/facturado/cancelado.
/** Cambia cantidad y/o modificadores de un item ya agregado. El backend
 *  recalcula precio unitario y subtotal (mig. 033). */
export const personalizarItemPedido = (
  pedidoId: number,
  itemId: number,
  modificadores: NonNullable<PedidoItem['modificadores']>,
) => api.patch<{ ok: boolean; item_id: number; precio_unitario: number; subtotal: number }>(
  `/api/pedidos/${pedidoId}/items/${itemId}`, { modificadores },
);

export const actualizarItemPedido = (pedidoId: number, itemId: number, cantidad: number) =>
  api.patch<{ ok: true; item_id: number; cantidad: number; subtotal: number }>(
    `/api/pedidos/${pedidoId}/items/${itemId}`,
    { cantidad },
  );

// DELETE /api/pedidos/:id/items/:itemId — elimina una línea del pedido.
// Recalcula totales del lado del backend.
export const eliminarItemPedido = (pedidoId: number, itemId: number) =>
  api.del<{ ok: true; item_id: number }>(`/api/pedidos/${pedidoId}/items/${itemId}`);

export const listPedidos = (opts: { sede_id?: number; estado?: PedidoEstado; activos?: boolean } = {}) => {
  const p = new URLSearchParams();
  if (opts.sede_id) p.set('sede_id', String(opts.sede_id));
  if (opts.estado) p.set('estado', opts.estado);
  if (opts.activos) p.set('activos', 'true');
  const qs = p.toString();
  return api.get<Pedido[]>(`/api/pedidos${qs ? `?${qs}` : ''}`);
};

export const getPedido = (id: number) =>
  api.get<PedidoConItems>(`/api/pedidos/${id}`);

export const cambiarEstadoPedido = (id: number, estado: 'en_preparacion' | 'listo' | 'entregado' | 'cancelado', notas?: string) =>
  api.patch<Pedido>(`/api/pedidos/${id}`, { estado, notas });

export const facturarPedido = (id: number, propina = 0, clienteId?: number) =>
  api.postIdempotente<{ ok: true; venta_id: number; total: number; prefijo: string; folio: number }>(
    `/api/pedidos/${id}/facturar`, { propina, cliente_id: clienteId }
  );

// ---------------------------------------------------------------------------
// Pago de pedido. El backend acepta dos formatos y los detecta automático
// (ver routes/pedidos.js):
//
//   Legacy (campos contables planos):
//     { pagos: [{ metodo, monto, medio_pago_dian?, referencia? }] }
//
//   Nuevo módulo payments (voucher amarrado a transaction_id):
//     { pagos: [{ transaction_id }] }
//
// IMPORTANTE: no mezclar formatos en el mismo request. Si alguno trae
// transaction_id, TODOS deben traer transaction_id.
// ---------------------------------------------------------------------------
export type PagoLegacy = {
  metodo: string;
  monto: number;
  medio_pago_dian?: string;
  referencia?: string;
};
export type PagoNuevo = { transaction_id: number };
export type PagarPedidoInput = PagoLegacy[] | PagoNuevo[];

export const pagarPedido = (id: number, pagos: PagarPedidoInput) =>
  api.postIdempotente<{ ok: true; totalPagado: number; pedido_id: number }>(
    `/api/pedidos/${id}/pagar`,
    { pagos }
  );

/**
 * Actualiza quién retira el pedido (mig. 038).
 *
 * Va aparte de cambiarEstadoPedido porque ese endpoint exige un estado válido:
 * para poner un nombre habría que mandar un estado que no se quiere cambiar.
 * Mandar cadena vacía BORRA el dato; omitir el campo lo deja como está.
 */
export const actualizarClientePedido = (
  id: number,
  data: { cliente_nombre?: string; cliente_telefono?: string },
) => api.patch<Pedido>(`/api/pedidos/${id}/cliente`, data);
